import {
	columnVisibilityFeature,
	createColumnHelper,
	metaHelper,
	rowSortingFeature,
	type SortingState,
	tableFeatures,
} from '@tanstack/react-table';

import type { DamageType } from '@/common/damage';
import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import type { DataTableColumnMeta } from '@/components/data-table/data-table-features';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { t } from '@/i18n';

export interface DamagePickerTableMeta {
	damageTypeLabel: (damage: DamageType) => string | undefined;
	materialLabel: (damage: DamageType) => string | undefined;
	isSelected: (damage: DamageType) => boolean;
	onToggle: (damage: DamageType) => void;
}

// The rows are sorted by `sortPickerDamages` before they reach the table (`manualSorting`), so no
// sorted row model or sort fns are registered.
export const damagePickerTableFeatures = tableFeatures({
	columnVisibilityFeature,
	rowSortingFeature,
	tableMeta: metaHelper<DamagePickerTableMeta>(),
	columnMeta: metaHelper<DataTableColumnMeta>(),
});

type PickerLabels = Pick<DamagePickerTableMeta, 'damageTypeLabel' | 'materialLabel'>;

/** The value each sortable column shows, so rows sort the way they read. */
const pickerSortValue: Record<string, (damage: DamageType, labels: PickerLabels) => string | undefined> = {
	type: (damage, labels) => labels.damageTypeLabel(damage),
	subAsset: (damage) => damage.subAsset?.name || damage.asset?.name,
	material: (damage, labels) => labels.materialLabel(damage),
	class: (damage) => damage.class,
};

/**
 * Sorts the picker's rows in the browser. The picker fetches a fixed set of damages and filters it
 * client-side, so sorting on the server would change which damages can be picked at all. Empty
 * values stay last in both directions; numbers inside values compare numerically ("2" before "10").
 */
export function sortPickerDamages(damages: DamageType[], sorting: SortingState, labels: PickerLabels): DamageType[] {
	const [sort] = sorting;
	const getValue = sort && pickerSortValue[sort.id];
	if (!getValue) return damages;
	const direction = sort.desc ? -1 : 1;
	return [...damages].sort((a, b) => {
		const left = getValue(a, labels) || '';
		const right = getValue(b, labels) || '';
		if (!left || !right) return left ? -1 : right ? 1 : 0;
		return direction * left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' });
	});
}

const columnHelper = createColumnHelper<typeof damagePickerTableFeatures, DamageType>();

export const damagePickerColumns = columnHelper.columns([
	// Multi-select only; hidden through column visibility otherwise.
	columnHelper.display({
		id: 'select',
		header: () => <span className="sr-only">{t('common.actions.select')}</span>,
		cell: ({ row, table }) => (
			<Checkbox
				checked={table.options.meta?.isSelected(row.original)}
				onCheckedChange={() => table.options.meta?.onToggle(row.original)}
				// The row click toggles too; stop it so one click is one toggle.
				onClick={(e) => e.stopPropagation()}
				aria-label={t('finance.damages.select.selectAriaLabel')}
			/>
		),
		meta: { headerClassName: 'w-10' },
	}),
	// A UUID, so sorting it is meaningless.
	columnHelper.accessor('id', {
		id: 'id',
		header: () => t('finance.damages.select.table.id'),
		enableSorting: false,
		meta: { className: 'text-foreground font-medium' },
	}),
	columnHelper.accessor((row) => row.damageTypeId, {
		id: 'type',
		header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.labels.type')} />,
		cell: ({ row, table }) => table.options.meta?.damageTypeLabel(row.original) || '-',
		meta: { className: 'text-muted-foreground' },
	}),
	// Shows the sub-asset, falling back to the asset for asset-level damages. Sorting is by sub-asset
	// name, so asset-level damages (no sub-asset) sort last ascending and first descending.
	columnHelper.accessor((row) => row.subAsset?.name || row.asset?.name, {
		id: 'subAsset',
		header: ({ column }) => <DataTableColumnHeader column={column} title={t('finance.damages.assetSubAsset')} />,
		cell: ({ getValue }) => getValue() || '-',
		meta: { className: 'text-muted-foreground' },
	}),
	columnHelper.accessor((row) => row.materialId, {
		id: 'material',
		header: ({ column }) => <DataTableColumnHeader column={column} title={t('finance.damages.material')} />,
		cell: ({ row, table }) => table.options.meta?.materialLabel(row.original) || '-',
		meta: { className: 'text-muted-foreground' },
	}),
	columnHelper.accessor('class', {
		id: 'class',
		header: ({ column }) => <DataTableColumnHeader column={column} title={t('finance.damages.class')} />,
		meta: { className: 'text-muted-foreground' },
	}),
	// Single-select only; the whole row is clickable, the button just makes that obvious.
	columnHelper.display({
		id: 'selectAction',
		header: () => <span className="sr-only">{t('common.actions.select')}</span>,
		cell: () => (
			<Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white h-7">
				{t('common.actions.select')}
			</Button>
		),
		meta: { headerClassName: 'w-20' },
	}),
]);
